import React, { ChangeEvent, MouseEvent, useState } from "react";
import { useAlert } from "../../ui/components/ConfirmDialog";
import StepUpCodeField, { toEmailCode } from "../authentication/StepUpCodeField";
import type {
  AccountProof,
  ChangePasswordRequest,
  ChangeUsernameRequest,
  EmailChangeStarted,
  StepUpProof,
  UpdateEmailRequest,
} from "../../services/rtkApi/authServiceApi";
import styles from "./profile-box.module.css";

interface EditableUserInfoProps {
  username: string;
  email: string;
  role: string;
  dateSigned: string;
  usernameChanged: boolean;
  // false for an account made without a password: it confirms changes with a
  // code from its email. absent means a password account, as before
  hasPassword?: boolean;
  // false when password sign-in is turned off for this account
  passwordLoginAvailable?: boolean;
  onChangeUsername: (data: ChangeUsernameRequest) => void;
  // resolves with changeId when auth mailed a code to the new address first
  onChangeEmail: (data: UpdateEmailRequest) => Promise<EmailChangeStarted | null>;
  onConfirmEmailChange: (data: { changeId: string; code: string }) => void;
  onChangePassword: (data: ChangePasswordRequest) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const EditableUserInfo: React.FC<EditableUserInfoProps> = ({
  username,
  email,
  role,
  dateSigned,
  usernameChanged,
  hasPassword = true,
  passwordLoginAvailable = true,
  onChangeUsername,
  onChangeEmail,
  onConfirmEmailChange,
  onChangePassword,
  onCancel,
  isLoading,
}) => {
  const alert = useAlert();
  const [usernameData, setUsernameData] = useState({
    newUsername: username,
    password: "",
  });
  const [usernameProof, setUsernameProof] = useState<StepUpProof | null>(null);
  const [emailData, setEmailData] = useState({
    newEmail: email,
    password: "",
  });
  const [emailProof, setEmailProof] = useState<StepUpProof | null>(null);
  const [emailChange, setEmailChange] = useState<{ changeId: string; sentTo?: string } | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordProof, setPasswordProof] = useState<StepUpProof | null>(null);

  // the password when the account has one, otherwise the step-up proof
  const proofFrom = (password: string, stepUp: StepUpProof | null): AccountProof | null => {
    if (hasPassword) return password ? { password } : null;
    return stepUp;
  };

  const alertMissingProof = () =>
    hasPassword
      ? alert({ title: "Password required", message: "Enter your current password to confirm." })
      : alert({ title: "Code required", message: "Ask for a code and type it in to confirm." });

  const handleUsernameSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!usernameData.newUsername.trim()) {
      await alert({ title: "Invalid username", message: "Username cannot be empty." });
      return;
    }
    const proof = proofFrom(usernameData.password, usernameProof);
    if (!proof) {
      await alertMissingProof();
      return;
    }
    if (usernameData.newUsername === username) {
      await alert({ title: "No change", message: "New username is the same as the current one." });
      return;
    }
    onChangeUsername({ newUsername: usernameData.newUsername, ...proof });
  };

  const handleEmailSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!emailData.newEmail.trim()) {
      await alert({ title: "Invalid email", message: "Email cannot be empty." });
      return;
    }
    const proof = proofFrom(emailData.password, emailProof);
    if (!proof) {
      await alertMissingProof();
      return;
    }
    if (emailData.newEmail === email) {
      await alert({ title: "No change", message: "New email is the same as the current one." });
      return;
    }
    const started = await onChangeEmail({ newEmail: emailData.newEmail, ...proof });
    if (started?.changeId) {
      setEmailChange({ changeId: started.changeId, sentTo: started.sentTo });
      setEmailCode("");
    }
  };

  const handleEmailConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const code = toEmailCode(emailCode);
    if (!emailChange || !code) {
      await alert({ title: "Code required", message: "Type the 8-digit code from the email." });
      return;
    }
    onConfirmEmailChange({ changeId: emailChange.changeId, code });
  };

  const handlePasswordSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      await alert({ title: "Missing fields", message: "Fill in both password fields." });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      await alert({ title: "Passwords differ", message: "Passwords do not match." });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      await alert({ title: "Password too short", message: "Password must be at least 6 characters." });
      return;
    }
    onChangePassword({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleSetPasswordSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!passwordData.newPassword) {
      await alert({ title: "Missing fields", message: "Fill in both password fields." });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      await alert({ title: "Passwords differ", message: "Passwords do not match." });
      return;
    }
    // the rule auth applies to passwords chosen at signup
    if (passwordData.newPassword.length < 8) {
      await alert({ title: "Password too short", message: "Password must be at least 8 characters." });
      return;
    }
    if (!passwordProof) {
      await alertMissingProof();
      return;
    }
    onChangePassword({ newPassword: passwordData.newPassword, ...passwordProof });
  };

  return (
    <div className={styles.userInfo}>
      {/* username section */}
      <label className={styles.infoBox}>
        <strong>username:</strong>{" "}
        {usernameChanged ? (
          <span>{username} <em style={{ fontSize: "0.8em", color: "#888" }}>(already changed once)</em></span>
        ) : (
          <input
            name="newUsername"
            value={usernameData.newUsername}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setUsernameData({ ...usernameData, newUsername: e.target.value })
            }
            disabled={isLoading}
          />
        )}
      </label>
      {!usernameChanged && (
        <>
          {hasPassword ? (
            <label className={styles.infoBox}>
              <strong>confirm password:</strong>{" "}
              <input
                type="password"
                placeholder="your current password"
                value={usernameData.password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setUsernameData({ ...usernameData, password: e.target.value })
                }
                disabled={isLoading}
              />
            </label>
          ) : (
            <div className={styles.infoBox}>
              <strong>confirm with a code:</strong>{" "}
              <StepUpCodeField action="change_username" onProofChange={setUsernameProof} disabled={isLoading} />
            </div>
          )}
          <button
            className={styles.submitButton}
            onClick={handleUsernameSubmit}
            disabled={isLoading || usernameData.newUsername === username}
          >
            Save Username
          </button>
        </>
      )}

      {/* email section */}
      {emailChange ? (
        <>
          <div className={styles.infoBox}>
            We sent a code to {emailChange.sentTo || emailData.newEmail}. Type it here to confirm your
            new email address. Your email stays the same until you do.
          </div>
          <label className={styles.infoBox}>
            <strong>code:</strong>{" "}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="8-digit code"
              value={emailCode}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailCode(e.target.value)}
              disabled={isLoading}
            />
          </label>
          <button
            className={styles.submitButton}
            onClick={handleEmailConfirm}
            disabled={isLoading || !toEmailCode(emailCode)}
          >
            Confirm email
          </button>
        </>
      ) : (
        <>
          <label className={styles.infoBox}>
            <strong>email:</strong>{" "}
            <input
              name="newEmail"
              value={emailData.newEmail}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setEmailData({ ...emailData, newEmail: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          {hasPassword ? (
            <label className={styles.infoBox}>
              <strong>confirm password:</strong>{" "}
              <input
                type="password"
                placeholder="your current password"
                value={emailData.password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmailData({ ...emailData, password: e.target.value })
                }
                disabled={isLoading}
              />
            </label>
          ) : (
            <div className={styles.infoBox}>
              <strong>confirm with a code:</strong>{" "}
              <StepUpCodeField action="change_email" onProofChange={setEmailProof} disabled={isLoading} />
            </div>
          )}
          <button
            className={styles.submitButton}
            onClick={handleEmailSubmit}
            disabled={isLoading || emailData.newEmail === email}
          >
            Save Email
          </button>
        </>
      )}

      {/* password section */}
      {passwordLoginAvailable && hasPassword && (
        <>
          <label className={styles.infoBox}>
            <strong>current password:</strong>{" "}
            <input
              type="password"
              placeholder="******"
              value={passwordData.currentPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPasswordData({ ...passwordData, currentPassword: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          <label className={styles.infoBox}>
            <strong>new password:</strong>{" "}
            <input
              type="password"
              placeholder="******"
              value={passwordData.newPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPasswordData({ ...passwordData, newPassword: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          <label className={styles.infoBox}>
            <strong>confirm new password:</strong>{" "}
            <input
              type="password"
              placeholder="******"
              value={passwordData.confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          <button
            className={styles.submitButton}
            onClick={handlePasswordSubmit}
            disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword}
          >
            Save Password
          </button>
        </>
      )}
      {passwordLoginAvailable && !hasPassword && (
        <>
          <label className={styles.infoBox}>
            <strong>new password:</strong>{" "}
            <input
              type="password"
              autoComplete="new-password"
              placeholder="at least 8 characters"
              value={passwordData.newPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPasswordData({ ...passwordData, newPassword: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          <label className={styles.infoBox}>
            <strong>confirm new password:</strong>{" "}
            <input
              type="password"
              autoComplete="new-password"
              placeholder="the same again"
              value={passwordData.confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
              }
              disabled={isLoading}
            />
          </label>
          <div className={styles.infoBox}>
            <strong>confirm with a code:</strong>{" "}
            <StepUpCodeField action="set_password" onProofChange={setPasswordProof} disabled={isLoading} />
          </div>
          <button
            className={styles.submitButton}
            onClick={handleSetPasswordSubmit}
            disabled={isLoading || !passwordData.newPassword}
          >
            Set a password
          </button>
        </>
      )}

      {/* read-only info */}
      <label className={styles.infoBox}>
        <strong>role:</strong> {role}
      </label>
      <label className={styles.infoBox}>
        <strong>date signed:</strong> {dateSigned}
      </label>

      <button className={styles.cancelButton} onClick={onCancel} disabled={isLoading}>
        Cancel
      </button>
    </div>
  );
};

export default EditableUserInfo;
